import { Link } from "@tanstack/react-router";
import type { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from "react";

import styles from "./ButtonV2.module.css";

import { IconV2, type IconV2Name } from "@/components/atoms/IconV2";

export type ButtonV2Variant = "primary" | "secondary" | "ghost" | "destructive";
export type ButtonV2Size = "s" | "m";

type ButtonV2Base = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "disabled" | "type" | "aria-label"
> & {
  children: ReactNode;
  size?: ButtonV2Size;
  startIcon?: IconV2Name;
  endIcon?: IconV2Name;
  to?: string;
  onClick?: MouseEventHandler<HTMLElement>;
};

type Props = ButtonV2Base &
  (
    | { variant?: Exclude<ButtonV2Variant, "destructive">; inverse?: boolean }
    | { variant: "destructive"; inverse?: never }
  );

export const ButtonV2 = ({
  children,
  variant = "primary",
  size = "m",
  inverse = false,
  startIcon,
  endIcon,
  to,
  type = "button",
  disabled,
  onClick,
  "aria-label": ariaLabel,
}: Props) => {
  const className = `${styles.button} ${size === "s" ? styles.sizeS : styles.sizeM}`;
  const content = (
    <>
      {startIcon != null && <IconV2 name={startIcon} />}
      <span className={styles.label}>{children}</span>
      {endIcon != null && <IconV2 name={endIcon} />}
    </>
  );

  if (to != null) {
    return (
      <Link
        to={to}
        className={className}
        data-variant={variant}
        data-size={size}
        data-inverse={inverse ? "true" : undefined}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={className}
      data-variant={variant}
      data-size={size}
      data-inverse={inverse ? "true" : undefined}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
};
